import React, { useMemo, useContext, createContext, useCallback } from 'react';
import { PaginatedVisitors, VisitorCreateOrEditPayload } from 'clients/VisitorClient';
import { visitorClient, uploadClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { trackEvent } from 'utils/analytics';

import useReducerWithMiddleware from '../useReducerWithMiddleware';
import unauthorized from '../unauthorized';
import visitorsReducer, { VisitorsState, initialVisitorsState } from './reducer';
import {
    ADD_VISITOR_FAILED,
    ADD_VISITOR_REQUESTED,
    ADD_VISITOR_SUCCESS,
    DELETE_VISITOR_FAILED,
    DELETE_VISITOR_REQUESTED,
    DELETE_VISITOR_SUCCESS,
    EDIT_VISITOR_FAILED,
    EDIT_VISITOR_REQUESTED,
    EDIT_VISITOR_SUCCESS,
    VISITORS_FAILED,
    VISITORS_REQUESTED,
    VISITORS_SUCCESS,
    UPLOAD_VISITOR_CSV_REQUESTED,
    UPLOAD_VISITOR_CSV_SUCCESS,
    UPLOAD_VISITOR_CSV_FAILED,
    PROGRESS_UPLOAD_VISITOR_CSV_REQUESTED,
    PROGRESS_UPLOAD_VISITOR_CSV_SUCCESS,
    PROGRESS_UPLOAD_VISITOR_CSV_FAILED,
} from './constants';

interface VisitorsActions {
    getVisitors: (spotPk: number, currentFilters: VisitorFilters) => void;
    addVisitor: (visitor: VisitorCreateOrEditPayload, spotPk: number, currentFilters: VisitorFilters) => void;
    editVisitor: (
        visitor: VisitorCreateOrEditPayload,
        visitorPk: number,
        spotPk: number,
        currentFilters: VisitorFilters
    ) => void;
    deleteVisitor: (visitorPk: number, spotPk: number, currentFilters: VisitorFilters) => void;
    uploadVisitorCSV: (code: string, file: File, spotPk: number) => void;
    progressUploadVisitorCSV: (spotPk: number) => void;
}

type VisitorFilters = {
    page: number;
    expiring: boolean;
    search: string;
};

interface VisitorsContextType extends VisitorsState, VisitorsActions {}

export const VisitorsContext = createContext<VisitorsContextType | null>(null);

export const VisitorsProvider: React.FunctionComponent<React.PropsWithChildren<object>> = ({ children }) => {
    const [state, dispatch] = useReducerWithMiddleware(
        visitorsReducer,
        { ...initialVisitorsState },
        [],
        [unauthorized]
    );

    const getVisitors = useCallback(
        async (spotPk: number, currentFilters: VisitorFilters) => {
            const { page, expiring, search } = currentFilters;
            dispatch({ type: VISITORS_REQUESTED });

            try {
                const visitors: PaginatedVisitors = await visitorClient.getVisitors(spotPk, page, expiring, search);
                dispatch({ type: VISITORS_SUCCESS, payload: { visitors } });
            } catch (error) {
                dispatch({
                    type: VISITORS_FAILED,
                    payload: { error, message: error?.toString() || 'Fetching visitors failed.' },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const addVisitor = useCallback(
        async (visitor: VisitorCreateOrEditPayload, spotPk: number, currentFilters: VisitorFilters) => {
            const { page, expiring, search } = currentFilters;
            dispatch({ type: ADD_VISITOR_REQUESTED });
            try {
                const newVisitor = await visitorClient.addVisitor(visitor);
                const newVisitorCar =
                    newVisitor.car_details.plate +
                    (newVisitor.car_details.state ? ` - ${newVisitor.car_details.state}` : '');
                const visitors: PaginatedVisitors = await visitorClient.getVisitors(spotPk, page, expiring, search);

                dispatch({
                    type: ADD_VISITOR_SUCCESS,
                    payload: {
                        message: 'Added visitor with plate: ' + newVisitorCar,
                        visitors,
                    },
                });
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = (error as HTTPError<{ detail: string }>).cause?.detail || error.message;
                } else {
                    errorMessage = error?.toString() || 'Could not add visitor';
                }
                dispatch({
                    type: ADD_VISITOR_FAILED,
                    payload: { message: errorMessage },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const editVisitor = useCallback(
        async (
            visitor: VisitorCreateOrEditPayload,
            visitorPk: number,
            spotPk: number,
            currentFilters: VisitorFilters
        ) => {
            const { page, expiring, search } = currentFilters;
            dispatch({ type: EDIT_VISITOR_REQUESTED });
            try {
                const newVisitor = await visitorClient.editVisitor(visitor, visitorPk);
                const newVisitorCar =
                    newVisitor.car_details.plate +
                    (newVisitor.car_details.state ? ` - ${newVisitor.car_details.state}` : '');
                const visitors: PaginatedVisitors = await visitorClient.getVisitors(spotPk, page, expiring, search);

                dispatch({
                    type: EDIT_VISITOR_SUCCESS,
                    payload: {
                        message: 'Edited visitor with plate: ' + newVisitorCar,
                        visitors,
                    },
                });
            } catch (error) {
                let errorMessage;
                if (HTTPError.isHTTPError(error)) {
                    errorMessage = (error as HTTPError<{ detail: string }>).cause?.detail || error.message;
                } else {
                    errorMessage = error?.toString() || 'Could not edit visitor';
                }
                dispatch({
                    type: EDIT_VISITOR_FAILED,
                    payload: { message: errorMessage },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const deleteVisitor = useCallback(async (visitorPk: number, spotPk: number, currentFilters: VisitorFilters) => {
        const { page, expiring, search } = currentFilters;

        dispatch({ type: DELETE_VISITOR_REQUESTED });
        try {
            await visitorClient.deleteVisitor(visitorPk);
            const visitors: PaginatedVisitors = await visitorClient.getVisitors(spotPk, page, expiring, search);
            dispatch({
                type: DELETE_VISITOR_SUCCESS,
                payload: {
                    message: 'Deleted visitor successfully',
                    visitors,
                },
            });
        } catch (error) {
            dispatch({
                type: DELETE_VISITOR_FAILED,
                payload: { message: error?.toString() || 'Could not delete visitor' },
            });
        }
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const uploadVisitorCSV = useCallback(
        async (code: string, file: File, spotPk: number) => {
            dispatch({ type: UPLOAD_VISITOR_CSV_REQUESTED });

            try {
                const uploadFileResponse = await uploadClient.uploadFile(file);
                const uploadVisitorPayload = {
                    code: code,
                    file: file,
                    spot: spotPk,
                    csv_url: uploadFileResponse.result,
                };
                const response = await visitorClient.uploadVisitorsCSV(uploadVisitorPayload);
                if (response.result === 'success') {
                    dispatch({ type: UPLOAD_VISITOR_CSV_SUCCESS, payload: { message: response.message } });
                    trackEvent('Uploaded visitor CSV file');
                } else {
                    dispatch({ type: UPLOAD_VISITOR_CSV_FAILED, payload: { message: response.message } });
                }
            } catch (error) {
                dispatch({
                    type: UPLOAD_VISITOR_CSV_FAILED,
                    payload: { message: error?.toString() || 'Uploading visitors CSV failed.' },
                });
                trackEvent('Uploaded visitor CSV file failed');
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const progressUploadVisitorCSV = useCallback(async (spotPk: number) => {
        try {
            dispatch({ type: PROGRESS_UPLOAD_VISITOR_CSV_REQUESTED });
            const response = await visitorClient.progressUploadVisitorsCSV(spotPk);
            dispatch({ type: PROGRESS_UPLOAD_VISITOR_CSV_SUCCESS, payload: { uploadCsvProgress: response.progress } });
        } catch (error) {
            dispatch({
                type: PROGRESS_UPLOAD_VISITOR_CSV_FAILED,
                payload: { message: error?.toString() || 'Could not check progress of upload CSV' },
            });
            trackEvent('Check progress of visitor CSV upload failed');
        }
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo<VisitorsContextType>(() => {
        return {
            ...state,
            getVisitors,
            addVisitor,
            editVisitor,
            deleteVisitor,
            uploadVisitorCSV,
            progressUploadVisitorCSV,
        };
    }, [state, getVisitors, addVisitor, editVisitor, deleteVisitor, uploadVisitorCSV, progressUploadVisitorCSV]);
    return <VisitorsContext.Provider value={value}>{children}</VisitorsContext.Provider>;
};

export const useVisitors = () => {
    const context = useContext(VisitorsContext);
    if (!context) {
        throw new Error('Error: useVisitors should be wrapped by VisitorsProvider.');
    }
    return context;
};
